import { useEffect, useState, useCallback } from "react";
import api, { apiError } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { NAV } from "@/data/site";
import { toast } from "sonner";
import {
  LayoutPanelTop, Eye, EyeOff, Plus, Trash2, RotateCcw, FileText,
  Image as ImageIcon, Loader2, ExternalLink, Globe, Layers,
} from "lucide-react";

const TABS = [
  { id: "pages", label: "Pages", icon: Globe },
  { id: "sections", label: "Hidden Sections", icon: Layers },
  { id: "content", label: "Edited Content", icon: FileText },
];

const NEW_PAGE = { slug: "", title: "", title_hi: "", subtitle: "", subtitle_hi: "", body: "", body_hi: "", hero_image: "", nav: true, published: true };

export default function SiteEditor() {
  const { loadOverrides, loadPages } = useApp();
  const [tab, setTab] = useState("pages");
  const [overrides, setOverrides] = useState({});
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(NEW_PAGE);
  const [creating, setCreating] = useState(false);
  const [showForm, setShowForm] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [ov, pg] = await Promise.all([api.get("/overrides"), api.get("/pages/manage")]);
      setOverrides(ov.data);
      setPages(pg.data);
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const syncPublic = async () => { await Promise.all([loadOverrides(), loadPages()]); };

  // ---- built-in page visibility (override navhide.<key>) ----
  const navHidden = (key) => overrides[`navhide.${key}`]?.value === "1";
  const toggleNav = async (key) => {
    try {
      if (navHidden(key)) await api.delete(`/overrides/navhide.${key}`);
      else await api.put(`/overrides/navhide.${key}`, { value: "1" });
      await refresh(); await syncPublic();
      toast.success("Menu updated");
    } catch (e) { toast.error(apiError(e)); }
  };

  // ---- custom pages ----
  const createPage = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      await api.post("/pages", form);
      toast.success("Page created");
      setForm(NEW_PAGE); setShowForm(false);
      await refresh(); await syncPublic();
    } catch (e) { toast.error(apiError(e)); }
    finally { setCreating(false); }
  };
  const patchPage = async (id, patch) => {
    try { await api.put(`/pages/${id}`, patch); await refresh(); await syncPublic(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const deletePage = async (id, title) => {
    if (!window.confirm(`Delete page "${title}"? This cannot be undone.`)) return;
    try { await api.delete(`/pages/${id}`); toast.success("Page deleted"); await refresh(); await syncPublic(); }
    catch (e) { toast.error(apiError(e)); }
  };

  // ---- overrides (sections + content) ----
  const resetOverride = async (key) => {
    try { await api.delete(`/overrides/${key}`); await refresh(); await syncPublic(); toast.success("Restored to default"); }
    catch (e) { toast.error(apiError(e)); }
  };
  const hiddenSections = Object.entries(overrides).filter(([k, v]) => k.startsWith("sec.") && v.value === "1");
  const contentEdits = Object.entries(overrides).filter(([k]) => !k.startsWith("sec.") && !k.startsWith("navhide."));
  const resetAllContent = async () => {
    if (!window.confirm("Reset ALL edited text & images back to their original defaults?")) return;
    try {
      await Promise.all(contentEdits.map(([k]) => api.delete(`/overrides/${k}`)));
      await refresh(); await syncPublic(); toast.success("All content reset to defaults");
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div data-testid="site-editor">
      <div className="flex items-center gap-3 mb-2">
        <div className="grid place-items-center w-11 h-11 rounded-xl bg-[#017471] text-white"><LayoutPanelTop className="w-5 h-5" /></div>
        <div>
          <h1 className="text-2xl font-extrabold text-[#0B3B39]">Pages & Content Control</h1>
          <p className="text-sm text-slate-500">Show/hide pages & sections, add custom pages, and roll back any edit across the whole site.</p>
        </div>
      </div>

      <div className="flex gap-2 border-b border-teal-100 mt-6 mb-6">
        {TABS.map((tb) => {
          const Icon = tb.icon;
          return (
            <button key={tb.id} onClick={() => setTab(tb.id)} data-testid={`se-tab-${tb.id}`}
              className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-semibold rounded-t-lg border-b-2 -mb-px transition-all ${tab === tb.id ? "border-[#017471] text-[#017471]" : "border-transparent text-slate-500 hover:text-teal-700"}`}>
              <Icon className="w-4 h-4" /> {tb.label}
            </button>
          );
        })}
      </div>

      {loading ? (
        <div className="grid place-items-center py-20 text-teal-500"><Loader2 className="w-7 h-7 animate-spin" /></div>
      ) : (
        <>
          {tab === "pages" && (
            <div className="space-y-8">
              {/* Built-in pages */}
              <div>
                <h3 className="text-sm font-bold uppercase tracking-wider text-slate-500 mb-3">Built-in pages (show / hide in menu)</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {NAV.map((n) => {
                    const hidden = navHidden(n.key);
                    return (
                      <div key={n.key} className="oj-card p-4 flex items-center justify-between gap-3" data-testid={`se-page-${n.key}`}>
                        <div className="min-w-0">
                          <div className="font-bold text-[#0B3B39] capitalize truncate">{n.key.replace("-", " ")}</div>
                          <div className="text-xs text-slate-400 font-mono truncate">{n.path}</div>
                        </div>
                        <button onClick={() => toggleNav(n.key)} data-testid={`se-page-toggle-${n.key}`}
                          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold whitespace-nowrap ${hidden ? "bg-amber-100 text-amber-700 border border-amber-300" : "bg-teal-50 text-teal-700 border border-teal-200"}`}>
                          {hidden ? <><EyeOff className="w-3.5 h-3.5" /> Hidden</> : <><Eye className="w-3.5 h-3.5" /> In menu</>}
                        </button>
                      </div>
                    );
                  })}
                </div>
                <p className="text-xs text-slate-400 mt-2">Hidden pages are removed from the website menu. The admin can restore them anytime.</p>
              </div>

              {/* Custom pages */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-bold uppercase tracking-wider text-slate-500">Custom pages</h3>
                  <button onClick={() => setShowForm((s) => !s)} data-testid="se-new-page-btn" className="oj-btn-gold !py-2 !px-4 !text-xs"><Plus className="w-4 h-4" /> New Page</button>
                </div>

                {showForm && (
                  <form onSubmit={createPage} className="oj-card p-6 mb-4 space-y-4" data-testid="se-new-page-form">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <input required placeholder="URL slug (e.g. our-partners)" value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} className="oj-input" data-testid="se-form-slug" />
                      <input required placeholder="Title (English)" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="oj-input" data-testid="se-form-title" />
                      <input placeholder="शीर्षक (Hindi)" value={form.title_hi} onChange={(e) => setForm({ ...form, title_hi: e.target.value })} className="oj-input" data-testid="se-form-title-hi" />
                      <input placeholder="Hero image URL (optional)" value={form.hero_image} onChange={(e) => setForm({ ...form, hero_image: e.target.value })} className="oj-input" data-testid="se-form-image" />
                      <input placeholder="Subtitle (English)" value={form.subtitle} onChange={(e) => setForm({ ...form, subtitle: e.target.value })} className="oj-input sm:col-span-2" data-testid="se-form-subtitle" />
                    </div>
                    <textarea rows={5} placeholder="Body — one paragraph per line (English)" value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} className="oj-input resize-none" data-testid="se-form-body" />
                    <textarea rows={4} placeholder="मुख्य सामग्री (Hindi, optional)" value={form.body_hi} onChange={(e) => setForm({ ...form, body_hi: e.target.value })} className="oj-input resize-none" data-testid="se-form-body-hi" />
                    <div className="flex flex-wrap items-center gap-5">
                      <label className="flex items-center gap-2 text-sm text-slate-600"><input type="checkbox" checked={form.nav} onChange={(e) => setForm({ ...form, nav: e.target.checked })} data-testid="se-form-nav" /> Show in menu</label>
                      <label className="flex items-center gap-2 text-sm text-slate-600"><input type="checkbox" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} data-testid="se-form-published" /> Published</label>
                      <button disabled={creating} className="oj-btn-gold !py-2 !px-5 !text-sm ml-auto" data-testid="se-form-submit">
                        {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Create Page
                      </button>
                    </div>
                  </form>
                )}

                {pages.length === 0 ? (
                  <div className="oj-card p-8 text-center text-slate-400 text-sm">No custom pages yet. Click "New Page" to add one.</div>
                ) : (
                  <div className="space-y-3">
                    {pages.map((p) => (
                      <div key={p.id} className="oj-card p-4 flex flex-wrap items-center justify-between gap-3" data-testid={`se-custom-${p.slug}`}>
                        <div className="min-w-0">
                          <div className="font-bold text-[#0B3B39] truncate">{p.title}</div>
                          <a href={`/p/${p.slug}`} target="_blank" rel="noreferrer" className="text-xs text-teal-600 font-mono inline-flex items-center gap-1 hover:underline">/p/{p.slug} <ExternalLink className="w-3 h-3" /></a>
                        </div>
                        <div className="flex items-center gap-2">
                          <button onClick={() => patchPage(p.id, { published: !p.published })} data-testid={`se-custom-pub-${p.slug}`}
                            className={`px-3 py-1.5 rounded-lg text-xs font-bold ${p.published ? "bg-teal-50 text-teal-700 border border-teal-200" : "bg-slate-100 text-slate-500 border border-slate-200"}`}>
                            {p.published ? "Published" : "Draft"}
                          </button>
                          <button onClick={() => patchPage(p.id, { nav: !p.nav })} data-testid={`se-custom-nav-${p.slug}`}
                            className={`px-3 py-1.5 rounded-lg text-xs font-bold ${p.nav ? "bg-amber-100 text-amber-700 border border-amber-300" : "bg-slate-100 text-slate-500 border border-slate-200"}`}>
                            {p.nav ? "In menu" : "Not in menu"}
                          </button>
                          <button onClick={() => deletePage(p.id, p.title)} data-testid={`se-custom-del-${p.slug}`} className="grid place-items-center w-8 h-8 rounded-lg bg-red-50 text-red-500 hover:bg-red-100 border border-red-200"><Trash2 className="w-4 h-4" /></button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {tab === "sections" && (
            <div>
              {hiddenSections.length === 0 ? (
                <div className="oj-card p-10 text-center text-slate-400 text-sm">No sections are hidden. Open any page, click "Edit Page", then "Hide" on a section to remove it from visitors.</div>
              ) : (
                <div className="space-y-3">
                  {hiddenSections.map(([k]) => (
                    <div key={k} className="oj-card p-4 flex items-center justify-between gap-3" data-testid={`se-hidden-${k}`}>
                      <div className="flex items-center gap-3 min-w-0">
                        <Layers className="w-4 h-4 text-amber-500 shrink-0" />
                        <span className="font-mono text-sm text-[#0B3B39] truncate">{k.replace("sec.", "")}</span>
                      </div>
                      <button onClick={() => resetOverride(k)} data-testid={`se-hidden-restore-${k}`} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold bg-amber-500 text-[#014845]"><RotateCcw className="w-3.5 h-3.5" /> Restore</button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {tab === "content" && (
            <div>
              {contentEdits.length > 0 && (
                <div className="flex justify-end mb-4">
                  <button onClick={resetAllContent} data-testid="se-reset-all" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-bold bg-red-50 text-red-600 border border-red-200 hover:bg-red-100"><RotateCcw className="w-4 h-4" /> Reset all edits</button>
                </div>
              )}
              {contentEdits.length === 0 ? (
                <div className="oj-card p-10 text-center text-slate-400 text-sm">No custom edits yet. Open any page, click "Edit Page", then click text or hover images to change them.</div>
              ) : (
                <div className="space-y-3">
                  {contentEdits.map(([k, v]) => (
                    <div key={k} className="oj-card p-4 flex items-center justify-between gap-3" data-testid={`se-content-${k}`}>
                      <div className="flex items-center gap-3 min-w-0 flex-1">
                        {v.type === "image" ? <ImageIcon className="w-4 h-4 text-teal-500 shrink-0" /> : <FileText className="w-4 h-4 text-teal-500 shrink-0" />}
                        <div className="min-w-0">
                          <div className="font-mono text-xs text-slate-400 truncate">{k}</div>
                          <div className="text-sm text-[#0B3B39] truncate">{v.type === "image" ? "Replaced image" : v.value}</div>
                        </div>
                      </div>
                      <button onClick={() => resetOverride(k)} data-testid={`se-content-reset-${k}`} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold bg-slate-100 text-slate-600 border border-slate-200 hover:bg-slate-200 whitespace-nowrap"><RotateCcw className="w-3.5 h-3.5" /> Reset</button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
